import type { QualityIssue } from "@/types";

import { DEFAULT_QUALITY_WEIGHTS, type QualityWeights } from "./weights";

/**
 * Weighted Quality Score:
 *   score = 100 − Σ(rule_weight × rows_with_issue / total_rows), clamped 0–100.
 *
 * "rows_with_issue" is the count of distinct product ids flagged by that rule.
 */

/** Distinct product-id count per rule id. */
export function ruleCounts(issues: QualityIssue[]): Record<string, number> {
  const byRule = new Map<string, Set<string>>();
  for (const issue of issues) {
    const set = byRule.get(issue.rule_id) ?? new Set<string>();
    set.add(issue.product_id);
    byRule.set(issue.rule_id, set);
  }
  const counts: Record<string, number> = {};
  for (const [rule, set] of byRule) counts[rule] = set.size;
  return counts;
}

export function computeQualityScore(
  issues: QualityIssue[],
  totalRows: number,
  weights: QualityWeights = DEFAULT_QUALITY_WEIGHTS,
): number {
  if (totalRows <= 0) return 100;
  const counts = ruleCounts(issues);
  let penalty = 0;
  for (const [rule, count] of Object.entries(counts)) {
    penalty += (weights[rule] ?? 0) * (count / totalRows);
  }
  return Math.max(0, Math.min(100, Math.round(100 - penalty)));
}
